import React, { useState } from 'react';
import {
  ShieldAlert,
  Sparkles,
  TestTube2,
  CheckCircle2,
  Clock,
  Waves,
  Heart,
  Droplets,
  AlertTriangle,
  RotateCcw,
  Zap
} from 'lucide-react';
import { EarlyDeteriorationSignal } from '../types';
import { generateEarlyDeteriorationIntervention } from '../services/geminiService';

interface EarlyDeteriorationSentryCardProps {
  signal: EarlyDeteriorationSignal;
  onPlaceOrder: (signalId: string) => void;
  onUpdateNote?: (signalId: string, newNote: string) => void;
}

export const EarlyDeteriorationSentryCard: React.FC<EarlyDeteriorationSentryCardProps> = ({
  signal,
  onPlaceOrder,
  onUpdateNote
}) => {
  const [isSynthesizing, setIsSynthesizing] = useState<boolean>(false);
  const [localNote, setLocalNote] = useState<string>(signal.aiInterventionNote);

  const handleReanalyzeWithAI = async () => {
    setIsSynthesizing(true);
    const updated = await generateEarlyDeteriorationIntervention(
      signal.seniorName,
      signal.location,
      signal.trends
    );
    setLocalNote(updated);
    if (onUpdateNote) {
      onUpdateNote(signal.id, updated);
    }
    setIsSynthesizing(false);
  };

  return (
    <div className="bg-gradient-to-br from-slate-900/90 via-slate-900 to-rose-950/20 border-2 border-rose-500/40 rounded-2xl p-5 shadow-2xl relative overflow-hidden">
      {/* Ambient Pulsing Radar Watermark */}
      <div className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-rose-500/10 blur-2xl pointer-events-none" />

      {/* Top Banner: Sentry Alert Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3 border-b border-slate-800 gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-rose-500/20 border border-rose-500/50 flex items-center justify-center text-rose-400 shadow-md shadow-rose-950/40">
            <ShieldAlert className="h-5 w-5 animate-pulse text-rose-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-white text-sm tracking-wide">
                EARLY DETERIORATION SENTRY
              </span>
              <span className="bg-rose-500/20 text-rose-300 border border-rose-500/40 text-[9px] px-2 py-0.5 rounded font-mono font-bold tracking-wider uppercase">
                AI TRIAD TRIGGERED
              </span>
            </div>
            <p className="text-xs text-slate-300 mt-0.5">
              <strong className="text-white font-bold">{signal.seniorName}</strong> • {signal.location}
              <span className="text-slate-500 ml-2 font-mono text-[10px]">({signal.detectedAt})</span>
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className="flex items-center gap-2">
          {signal.orderPlaced ? (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs font-semibold">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
              <span>STAT Lab Order Active</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-medium">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
              <span>Intervention Recommended</span>
            </div>
          )}
        </div>
      </div>

      {/* 3 Subtle Parameter Trends Cluster */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-2">
          <span className="font-mono uppercase text-[10px] text-slate-400 tracking-wider flex items-center gap-1">
            <Zap className="h-3 w-3 text-amber-400" /> Subtle 3-Parameter Degradation Triad:
          </span>
          <span className="text-[10px] font-mono text-slate-500">Cross-Sensor Sensor Fusion</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {signal.trends.map((t, idx) => {
            const isGait = t.label.toLowerCase().includes('gait') || t.label.toLowerCase().includes('walking');
            const isBathroom = t.label.toLowerCase().includes('bathroom') || t.label.toLowerCase().includes('medication');
            const isHeart = t.label.toLowerCase().includes('heart') || t.label.toLowerCase().includes('pulse') || t.label.toLowerCase().includes('weight');

            return (
              <div
                key={idx}
                className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-1">
                    <span className="flex items-center gap-1.5">
                      {isGait && <Waves className="h-3.5 w-3.5 text-cyan-400" />}
                      {isBathroom && <Droplets className="h-3.5 w-3.5 text-amber-400" />}
                      {isHeart && <Heart className="h-3.5 w-3.5 text-rose-400" />}
                      <span>{t.label}</span>
                    </span>
                    <span
                      className={`text-[9px] px-1.5 py-0.2 rounded font-bold ${
                        t.severity === 'critical'
                          ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                          : t.severity === 'warning'
                          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                          : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                      }`}
                    >
                      {t.delta}
                    </span>
                  </div>

                  <div className="text-xs font-bold text-slate-200 mt-1">
                    Current: <span className="text-rose-400 font-mono">{t.current}</span>
                  </div>
                </div>

                <div className="text-[10px] text-slate-500 mt-1.5 font-mono">
                  Baseline: {t.baseline}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* AI Proactive Nursing Intervention Note */}
      <div className="mt-4 p-4 rounded-xl bg-rose-950/20 border border-rose-500/30 space-y-2 relative">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5 text-rose-300 font-bold text-[11px] tracking-wide">
            <Sparkles className="h-3.5 w-3.5 text-rose-400" />
            <span>AI PROACTIVE NURSING INTERVENTION (GEMINI 2.5 FLASH)</span>
          </div>

          <button
            onClick={handleReanalyzeWithAI}
            disabled={isSynthesizing}
            className="flex items-center gap-1 text-[10px] text-teal-300 hover:text-teal-200 font-mono disabled:opacity-50"
          >
            <RotateCcw className={`h-3 w-3 ${isSynthesizing ? 'animate-spin' : ''}`} />
            <span>{isSynthesizing ? 'Re-evaluating...' : 'Re-synthesize Note'}</span>
          </button>
        </div>

        <p className="text-xs text-slate-200 leading-relaxed font-sans font-medium">
          "{localNote}"
        </p>

        {/* Risk Badges */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-[10px] text-slate-400 font-mono">Imminent Flags:</span>
          {signal.riskSuspicions.map((risk, rIdx) => (
            <span
              key={rIdx}
              className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-900 text-rose-300 border border-rose-900/60"
            >
              {risk}
            </span>
          ))}
        </div>
      </div>

      {/* Order Urinalysis & Hydration Check Action Bar */}
      <div className="mt-4 pt-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
        <div className="text-[11px] text-slate-400">
          {signal.orderPlaced ? (
            <span className="text-emerald-400 font-mono flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" /> Requisition #{signal.orderRequisitionId || 'STAT-UA-709'} active • Ordered {signal.orderTimestamp || 'Today'}
            </span>
          ) : (
            <span className="text-amber-400 flex items-center gap-1 font-medium">
              <Clock className="h-3.5 w-3.5" /> Action required: Execute early countermeasures prior to ambulatory fall.
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {signal.orderPlaced ? (
            <button
              disabled
              className="px-4 py-2 rounded-xl bg-emerald-600/30 text-emerald-300 border border-emerald-500/40 text-xs font-bold flex items-center gap-1.5 cursor-default"
            >
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              <span>Urinalysis & Hydration Check Ordered</span>
            </button>
          ) : (
            <button
              onClick={() => onPlaceOrder(signal.id)}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-rose-600 to-amber-600 hover:from-rose-500 hover:to-amber-500 text-slate-950 font-extrabold text-xs transition flex items-center gap-1.5 shadow-lg shadow-rose-950/50 hover:scale-[1.02]"
            >
              <TestTube2 className="h-4 w-4" />
              <span>Order Urinalysis & Hydration Check</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
